import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
import { storefrontHostSchema } from "./storefront.js";
import { shopperAccountSchema, shopperOrderSchema } from "./storefront-account.js";
/**
 * DG-03 (ACC-07, docs/specs/shopper-accounts.md): the shopper's dashboard — profile, password and sign-in methods,
 * devices, address book, own orders (cancel, invoice), own reviews, deleting the account. Every route needs the
 * shopper's session and works only in its own shop.
 */
const tag = { tag: "storefront" };
const pub = { public: true };
const host = storefrontHostSchema;
const token = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
const password = z.string().min(8).max(128);
const ticket = z.string().min(1).max(100);
const signedIn = z.object({ host, token }).strict();
export const shopperUpdateProfile = defineEndpoint({
    ...tag,
    name: "shopperUpdateProfile",
    method: "POST",
    path: "/v1/storefront/account/me/profile",
    access: pub,
    summary: "Change the shopper's name, language and offers choice",
    body: z
        .object({
        host,
        token,
        name: z.string().trim().min(1).max(120).optional(),
        locale: z.enum(["en", "bn"]).optional(),
        marketingOptIn: z.boolean().optional(),
    })
        .strict(),
    status: 200,
    response: shopperAccountSchema,
    errors: ["UNAUTHENTICATED"],
});
export const shopperChangePassword = defineEndpoint({
    ...tag,
    name: "shopperChangePassword",
    method: "POST",
    path: "/v1/storefront/account/me/password",
    access: pub,
    summary: "Change the password (the current one is needed when there is one); signs out every other device",
    body: z.object({ host, token, current: z.string().max(128).optional(), password }).strict(),
    status: 204,
    errors: ["UNAUTHENTICATED", "INVALID_CREDENTIALS", "VALIDATION_FAILED"],
});
export const shopperChangeIdentifier = defineEndpoint({
    ...tag,
    name: "shopperChangeIdentifier",
    method: "POST",
    path: "/v1/storefront/account/me/identifier",
    access: pub,
    summary: "Add or change the email or mobile number, proven by a code ticket for the new one (and the password)",
    body: z
        .object({
        host,
        token,
        kind: z.enum(["email", "phone"]),
        value: z.string().trim().min(3).max(254),
        ticket,
        password: z.string().max(128).optional(),
    })
        .strict(),
    status: 200,
    response: shopperAccountSchema,
    errors: ["UNAUTHENTICATED", "INVALID_CREDENTIALS", "INVALID_OTP", "ACCOUNT_EXISTS", "VALIDATION_FAILED"],
});
export const shopperSessionViewSchema = z.object({
    id: z.uuid(),
    method: z.enum(["password", "phone_code", "google"]),
    /** "Chrome on Android"; empty when unknown. */
    device: z.string(),
    createdAt: z.iso.datetime(),
    lastSeenAt: z.iso.datetime(),
    /** The device asking. */
    current: z.boolean(),
});
export const shopperListSessions = defineEndpoint({
    ...tag,
    name: "shopperListSessions",
    method: "POST",
    path: "/v1/storefront/account/sessions",
    access: pub,
    summary: "The account's signed-in devices",
    body: signedIn,
    status: 200,
    response: z.object({ data: z.array(shopperSessionViewSchema) }),
    errors: ["UNAUTHENTICATED"],
});
export const shopperRevokeSession = defineEndpoint({
    ...tag,
    name: "shopperRevokeSession",
    method: "POST",
    path: "/v1/storefront/account/sessions/revoke",
    access: pub,
    summary: "Sign out one device, or every device but this one (`others`)",
    body: z.object({ host, token, id: z.uuid().optional(), others: z.boolean().optional() }).strict(),
    status: 204,
    errors: ["UNAUTHENTICATED", "NOT_FOUND"],
});
export const shopperDeleteAccount = defineEndpoint({
    ...tag,
    name: "shopperDeleteAccount",
    method: "POST",
    path: "/v1/storefront/account/delete",
    access: pub,
    summary: "Delete the account: its name, email, phone and sign-ins are erased; orders stay with the shop. Needs the password when there is one",
    body: z
        .object({ host, token, password: z.string().max(128).optional(), confirm: z.literal(true) })
        .strict(),
    status: 204,
    errors: ["UNAUTHENTICATED", "INVALID_CREDENTIALS"],
});
// Address book -----------------------------------------------------------------------------------------------------
export const shopperAddressSchema = z.object({
    id: z.uuid(),
    label: z.string(),
    name: z.string(),
    phone: z.string(),
    districtId: z.int(),
    upazilaId: z.int().nullable(),
    line: z.string(),
    isDefault: z.boolean(),
    /** "Mirpur, Dhaka" for lists. */
    area: z.string(),
    version: z.int(),
});
export const SHOPPER_ADDRESS_LIMIT = 10;
export const shopperListAddresses = defineEndpoint({
    ...tag,
    name: "shopperListAddresses",
    method: "POST",
    path: "/v1/storefront/account/addresses",
    access: pub,
    summary: "The account's saved delivery addresses, the default first",
    body: signedIn,
    status: 200,
    response: z.object({ data: z.array(shopperAddressSchema) }),
    errors: ["UNAUTHENTICATED"],
});
export const shopperSaveAddress = defineEndpoint({
    ...tag,
    name: "shopperSaveAddress",
    method: "POST",
    path: "/v1/storefront/account/addresses/save",
    access: pub,
    summary: `Add an address (at most ${String(SHOPPER_ADDRESS_LIMIT)}) or change one (with its version)`,
    body: z
        .object({
        host,
        token,
        id: z.uuid().optional(),
        version: z.int().min(1).optional(),
        label: z.string().trim().max(30).default(""),
        name: z.string().trim().min(1).max(80),
        phone: z.string().trim().min(10).max(20),
        districtId: z.int().positive(),
        upazilaId: z.int().positive().nullable().default(null),
        line: z.string().trim().min(3).max(300),
        isDefault: z.boolean().default(false),
    })
        .strict(),
    status: 200,
    response: shopperAddressSchema,
    errors: ["UNAUTHENTICATED", "VALIDATION_FAILED", "NOT_FOUND", "CONFLICT", "BUSINESS_RULE_VIOLATION"],
});
export const shopperDeleteAddress = defineEndpoint({
    ...tag,
    name: "shopperDeleteAddress",
    method: "POST",
    path: "/v1/storefront/account/addresses/delete",
    access: pub,
    summary: "Remove a saved address",
    body: z.object({ host, token, id: z.uuid() }).strict(),
    status: 204,
    errors: ["UNAUTHENTICATED", "NOT_FOUND"],
});
// Orders and reviews -------------------------------------------------------------------------------------------------
export const shopperCancelOrder = defineEndpoint({
    ...tag,
    name: "shopperCancelOrder",
    method: "POST",
    path: "/v1/storefront/account/orders/{number}/cancel",
    access: pub,
    summary: "Cancel one's own order while it is received or confirmed and nothing was paid online",
    params: z.object({ number: z.string().min(1).max(40) }),
    body: z.object({ host, token, reason: z.string().trim().max(200).default("") }).strict(),
    status: 200,
    response: shopperOrderSchema,
    errors: ["UNAUTHENTICATED", "NOT_FOUND", "BUSINESS_RULE_VIOLATION"],
});
export const shopperOrderInvoice = defineEndpoint({
    ...tag,
    name: "shopperOrderInvoice",
    method: "POST",
    path: "/v1/storefront/account/orders/{number}/invoice",
    access: pub,
    summary: "A short-lived link to the order's A4 invoice (PDF)",
    params: z.object({ number: z.string().min(1).max(40) }),
    body: signedIn,
    status: 200,
    response: z.object({ url: z.string(), expiresAt: z.iso.datetime() }),
    errors: ["UNAUTHENTICATED", "NOT_FOUND"],
});
export const shopperReviewSchema = z.object({
    id: z.uuid(),
    productName: z.string(),
    productSlug: z.string().nullable(),
    orderNumber: z.string(),
    rating: z.int(),
    text: z.string(),
    status: z.enum(["pending", "published", "hidden"]),
    reply: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const shopperMyReviews = defineEndpoint({
    ...tag,
    name: "shopperMyReviews",
    method: "POST",
    path: "/v1/storefront/account/my-reviews",
    access: pub,
    summary: "The reviews this shopper wrote in this shop, newest first",
    body: signedIn,
    status: 200,
    response: z.object({ data: z.array(shopperReviewSchema) }),
    errors: ["UNAUTHENTICATED"],
});
//# sourceMappingURL=storefront-account-dashboard.js.map